<template>
    <card-tab-panel
        name="emma-valuation"
        class="emma-valuation-inline-tab-panel"
        without-padding
        v-bind="$attrs"
        with-variable-height
    >
        <flex-container :gap="Size.md">
            <flex-container
                align-items="center"
                justify-content="center"
                :padding="Size.sm"
                :color="Color.gray100"
            >
                <text-heading sub>
                    Emma's waardeindicatie
                </text-heading>
            </flex-container>

            <flex-container
                v-if="reason"
                class="emma-valuation-inline-tab-panel__goal"
                align-items="center"
                justify-content="center"
            >
                <div>
                    <chip-label :color="Color.green500">
                        {{ reason }}
                    </chip-label>
                </div>
            </flex-container>

            <section class="emma-valuation-inline-tab-panel__section">
                <chip-label v-if="valuation" :color="Color.gray400">
                    {{ formatDate(valuation.valuationDate) }}
                </chip-label>
                <text-heading v-if="address" sub>
                    {{ address.streetname }}
                    <template v-if="address.streetnumber && address.streetnumbersuffix">
                        {{ address.streetnumber }}-{{ address.streetnumbersuffix }}
                    </template>
                    <template v-else>
                        {{ address.streetnumber }} {{ address.streetnumbersuffix }}
                    </template>
                </text-heading>
                <text-decorator
                    v-if="address"
                    bold
                    :size="FontSize.xs"
                    :color="Color.gray400"
                >
                    {{ address.postalCode }} {{ address.city }}
                </text-decorator>

                <!-- icons for showing property information -->
                <flex-container
                    v-if="valuation"
                    :gap="Size.sm"
                    justify-content="flex-start"
                >
                    <flex-container
                        size="auto"
                        align-items="center"
                        :gap="Size.xs"
                    >
                        <svg-icon :icon="IconSquare" :size="IconSize.sm" />
                        <text-decorator
                            bold
                            :size="FontSize.xs"
                        >
                            {{ formatNumber(valuation.innerSurface) }} M<sup>2</sup>
                        </text-decorator>
                    </flex-container>

                    <flex-container
                        size="auto"
                        align-items="center"
                        :gap="Size.xs"
                    >
                        <svg-icon :icon="IconSquareWithHouse" :size="IconSize.sm" />
                        <text-decorator
                            bold
                            :size="FontSize.xs"
                        >
                            {{ formatNumber(valuation.outerSurface) }} M<sup>2</sup>
                        </text-decorator>
                    </flex-container>
                    <flex-container
                        size="auto"
                        align-items="center"
                        :gap="Size.xs"
                    >
                        <svg-icon :icon="IconBed" :size="IconSize.sm" />
                        <text-decorator
                            bold
                            :size="FontSize.xs"
                        >
                            {{ formatNumber(valuation.rooms) }}
                        </text-decorator>
                    </flex-container>
                    <flex-container
                        size="auto"
                        align-items="center"
                        :gap="Size.xs"
                    >
                        <svg-icon
                            :icon="IconWall"
                            :size="IconSize.sm"
                            is-custom-icon
                        />
                        <text-decorator
                            bold
                            :size="FontSize.xs"
                        >
                            {{ valuation.year }}
                        </text-decorator>
                    </flex-container>
                </flex-container>
            </section>

            <!-- underline blocks for valuations -->
            <flex-container
                v-if="valuation"
                no-wrap
                :gap="null"
            >
                <underline-block :color="Color.orange500">
                    <flex-container
                        inline
                        :gap="Size.xs"
                        justify-content="center"
                    >
                        <text-decorator
                            bold
                            :color="Color.orange500"
                            :size="FontSize.xs"
                        >
                            {{ formatMoney(valuation.minValuation, { maximumFractionDigits: 0 }) }}
                        </text-decorator>
                        <text-decorator :size="FontSize.xs">
                            Minimaal
                        </text-decorator>
                    </flex-container>
                </underline-block>
                <underline-block :color="Color.yellow500">
                    <flex-container
                        inline
                        :gap="Size.xs"
                        justify-content="center"
                    >
                        <text-decorator
                            bold
                            :color="Color.yellow500"
                            :size="FontSize.xs"
                        >
                            {{ formatMoney(valuation.medianValuation, { maximumFractionDigits: 0 }) }}
                        </text-decorator>
                        <text-decorator :size="FontSize.xs">
                            Gemiddeld
                        </text-decorator>
                    </flex-container>
                </underline-block>
                <underline-block :color="Color.green500">
                    <flex-container
                        inline
                        :gap="Size.xs"
                        justify-content="center"
                    >
                        <text-decorator
                            bold
                            :color="Color.green500"
                            :size="FontSize.xs"
                        >
                            {{ formatMoney(valuation.maxValuation, { maximumFractionDigits: 0 }) }}
                        </text-decorator>
                        <text-decorator :size="FontSize.xs">
                            Maximaal
                        </text-decorator>
                    </flex-container>
                </underline-block>
                <underline-block v-if="latestWoz" :color="Color.blue500">
                    <flex-container
                        inline
                        :gap="Size.xs"
                        justify-content="center"
                    >
                        <text-decorator
                            bold
                            :color="Color.blue500"
                            :size="FontSize.xs"
                        >
                            {{ formatMoney(latestWoz, { maximumFractionDigits: 0 }) }}
                        </text-decorator>
                        <text-decorator :size="FontSize.xs">
                            WOZ
                        </text-decorator>
                    </flex-container>
                </underline-block>
            </flex-container>

            <!-- Self define action -->
            <flex-container justify-content="center" align-items="center">
                <text-decorator v-if="valuation" :size="FontSize.sm">
                    {{ formatNumber(valuation.confidence) }}% Betrouwbaarheid
                </text-decorator>
                <button-text v-if="!showForm" @click="showForm = true">
                    <text-decorator bold>
                        Zelf waarde invoeren
                    </text-decorator>
                </button-text>
                <button-text v-else @click="showForm = false">
                    <text-decorator bold>
                        Verberg waarde invoer
                    </text-decorator>
                </button-text>
            </flex-container>

            <div v-if="showForm" class="emma-valuation-inline-tab-panel__form">
                <input-group v-model="manualValuation">
                    <template #label>
                        <input-label>
                            Eigen waarde
                        </input-label>
                    </template>
                    <template #input>
                        <text-input>
                            <template #prefix>
                                <svg-icon :icon="IconEuro" :size="IconSize.sm" />
                            </template>
                        </text-input>
                    </template>
                </input-group>

                <button-block-confirm @loading="saveManualValuation">
                    <template #default>
                        Opslaan
                    </template>
                    <template #confirmation>
                        Bevestig opslaan
                    </template>
                </button-block-confirm>
            </div>

            <section class="emma-valuation-inline-tab-panel__images">
                <img alt="huis" :src="image">
                <static-google-maps
                    v-if="mapPosition"
                    :location="mapPosition"
                    map-id="c786f2a813f5eb6a"
                />
            </section>

            <flex-container v-if="woz" :gap="null">
                <!-- WOZ WAARDES -->
                <flex-container
                    align-items="center"
                    class="emma-valuation-inline-tab-panel__header"
                    :color="Color.gray100"
                >
                    <text-decorator bold :size="FontSize.sm">
                        WOZ waardes
                    </text-decorator>
                </flex-container>
                <flex-container
                    :padding="Size.md"
                >
                    <div class="emma-valuation-inline-tab-panel__woz-grid">
                        <div v-for="(wozValue, date) in woz.wozValues" :key="date">
                            <text-decorator bold>
                                {{ date.toString().substring(0, 4) }}:
                            </text-decorator>
                            <span>
                                {{ formatMoney(wozValue, { maximumFractionDigits: 0 }) }}
                            </span>
                        </div>
                    </div>
                </flex-container>

                <!-- Referentie transacties -->
                <flex-container
                    class="emma-valuation-inline-tab-panel__header"
                    align-items="center"
                    :color="Color.gray100"
                >
                    <text-decorator bold :size="FontSize.sm">
                        Referentie transacties
                    </text-decorator>
                </flex-container>
                <flex-container
                    v-if="transactions"
                    :gap="null"
                >
                    <flex-container
                        v-for="transaction in visibleTransactions"
                        :key="transaction.id"
                        class="emma-valuation-inline-tab-panel__reference-transaction"
                        justify-content="space-between"
                        :padding="Size.md"
                        no-wrap
                    >
                        <flex-container :gap="Size.xs" :size="9">
                            <text-decorator bold :size="FontSize.sm">
                                {{ transaction.streetname }}
                                <template v-if="transaction.streetnumber && transaction.streetnumbersuffix">
                                    {{ transaction.streetnumber }}-{{ transaction.streetnumbersuffix }}
                                </template>
                                <template v-else>
                                    {{ transaction.streetnumber }} {{ transaction.streetnumbersuffix }}
                                </template>
                                ,
                                {{ transaction.city }}
                            </text-decorator>
                            <text-decorator :size="FontSize.sm">
                                {{ formatDate(transaction.transactionDate) }} &#x2022; {{ formatMoney(transaction.transactionPriceLow, { maximumFractionDigits: 0 }) }} - {{ formatMoney(transaction.transactionPriceHigh, { maximumFractionDigits: 0 }) }} &#x2022; {{ formatNumber(transaction.innerSurface) }} M<sup>2</sup>
                            </text-decorator>
                        </flex-container>
                        <flex-container
                            align-items="center"
                            align-content="center"
                            justify-content="center"
                            :size="1"
                        >
                            <collapse-box-decorator horizontal-position="right" vertical-position="top-out">
                                <template #default="{ bindings }">
                                    <svg-icon
                                        v-bind="bindings"
                                        :icon="IconInfo"
                                        :color="Color.brandGreen"
                                    />
                                </template>
                                <template #content>
                                    <div class="emma-valuation-inline-tab-panel__reference-transaction-information">
                                        <data-group v-if="transaction.year">
                                            <data-label>Bouwjaar</data-label>
                                            <data-value>{{ transaction.year }}</data-value>
                                        </data-group>

                                        <data-group v-if="transaction.type">
                                            <data-label>Woningtype</data-label>
                                            <data-value>{{ transaction.type }}</data-value>
                                        </data-group>

                                        <data-group v-if="transaction.innerSurface">
                                            <data-label>Oppervlakte (binnen)</data-label>
                                            <data-value>{{ formatNumber(transaction.innerSurface) }} m<sup>2</sup></data-value>
                                        </data-group>

                                        <data-group v-if="transaction.outerSurface">
                                            <data-label>Oppervlakte (buiten)</data-label>
                                            <data-value>{{ formatNumber(transaction.outerSurface) }} m<sup>2</sup></data-value>
                                        </data-group>

                                        <data-group v-if="transaction.volume">
                                            <data-label>Inhoud</data-label>
                                            <data-value>{{ formatNumber(transaction.volume) }} m<sup>3</sup></data-value>
                                        </data-group>

                                        <data-group v-if="transaction.transactionDate">
                                            <data-label>Transactiedatum</data-label>
                                            <data-value>{{ formatDate(transaction.transactionDate) }}</data-value>
                                        </data-group>

                                        <data-group v-if="transaction.transactionPriceLow || transaction.transactionPriceHigh">
                                            <data-label>Transactieprijs</data-label>
                                            <data-value>
                                                {{ formatMoney(transaction.transactionPriceLow, { maximumFractionDigits: 0 }) }}
                                                -
                                                {{ formatMoney(transaction.transactionPriceHigh, { maximumFractionDigits: 0 }) }}
                                            </data-value>
                                        </data-group>

                                        <data-group v-if="transaction.indexedTransactionPriceLow || transaction.indexedTransactionPriceHigh">
                                            <data-label>Geïndexeerde transactieprijs</data-label>
                                            <data-value>
                                                {{ formatMoney(transaction.indexedTransactionPriceLow, { maximumFractionDigits: 0 }) }}
                                                -
                                                {{ formatMoney(transaction.indexedTransactionPriceHigh, { maximumFractionDigits: 0 }) }}
                                            </data-value>
                                        </data-group>

                                        <data-group v-if="transaction.distance">
                                            <data-label>Afstand</data-label>
                                            <data-value>{{ formatNumber(transaction.distance) }} m</data-value>
                                        </data-group>
                                    </div>
                                </template>
                            </collapse-box-decorator>
                        </flex-container>
                    </flex-container>
                    <flex-container justify-content="center" :padding="Size.sm">
                        <button-text v-if="!showMoreTransactions" @click="showMoreTransactions = true">
                            <svg-icon :icon="IconExpandMore" />
                            <span>Toon Meer</span>
                        </button-text>
                        <button-text v-else @click="showMoreTransactions = false">
                            <svg-icon :icon="IconExpandLess" />
                            <span>Toon Minder</span>
                        </button-text>
                    </flex-container>
                </flex-container>

                <!-- Buurt info -->
                <flex-container
                    v-if="location"
                    align-items="center"
                    class="emma-valuation-inline-tab-panel__header"
                    :color="Color.gray100"
                >
                    <text-decorator bold :size="FontSize.sm">
                        Buurtinfo
                    </text-decorator>
                </flex-container>
                <flex-container
                    :padding="Size.md"
                >
                    <div
                        v-if="householdComposition"
                        class="emma-valuation-inline-tab-panel__neighbourhood-information"
                    >
                        <text-decorator :size="FontSize.sm" bold>
                            Huishoudens
                        </text-decorator>
                        <flex-container :gap="null" justify-content="space-between">
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                Totaal aantal:
                            </text-decorator>
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(householdComposition.Total) }}
                            </text-decorator>
                        </flex-container>
                        <flex-container :gap="null" justify-content="space-between">
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                Alleenwonend:
                            </text-decorator>
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(householdComposition.Single) }}
                            </text-decorator>
                        </flex-container>
                        <flex-container :gap="null" justify-content="space-between">
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                Gezin met kinderen:
                            </text-decorator>
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(householdComposition.WithKids) }}
                            </text-decorator>
                        </flex-container>
                        <flex-container :gap="null" justify-content="space-between">
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                Gezin zonder kinderen:
                            </text-decorator>
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(householdComposition.WithoutKids) }}
                            </text-decorator>
                        </flex-container>
                        <flex-container :gap="null" justify-content="space-between">
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                Gemiddelde grootte:
                            </text-decorator>
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(householdComposition.AverageHouseholdSize, 1) }}
                            </text-decorator>
                        </flex-container>
                    </div>

                    <div
                        v-if="inhabitants"
                        class="emma-valuation-inline-tab-panel__neighbourhood-information"
                    >
                        <text-decorator :size="FontSize.sm" bold>
                            Inwoners
                        </text-decorator>
                        <flex-container
                            :gap="null"
                            justify-content="space-between"
                            align-items="flex-start"
                        >
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                Totaal aantal:
                            </text-decorator>
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(inhabitants.Total) }}
                            </text-decorator>
                        </flex-container>
                        <flex-container :gap="null" justify-content="space-between">
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                Mannen:
                            </text-decorator>
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(inhabitants.Men) }}
                            </text-decorator>
                        </flex-container>
                        <flex-container :gap="null" justify-content="space-between">
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                Vrouwen:
                            </text-decorator>
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(inhabitants.Women) }}
                            </text-decorator>
                        </flex-container>
                    </div>

                    <div v-if="neighborhood" class="emma-valuation-inline-tab-panel__neighbourhood-information">
                        <text-decorator :size="FontSize.sm" bold>
                            Leeftijden
                        </text-decorator>
                        <flex-container :gap="null" justify-content="space-between">
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                0 t/m 14 jaar:
                            </text-decorator>
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(neighborhood.Age['0-14']) }}
                            </text-decorator>
                        </flex-container>
                        <flex-container :gap="null" justify-content="space-between">
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                15 t/m 24 jaar:
                            </text-decorator>
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(neighborhood.Age['15-24']) }}
                            </text-decorator>
                        </flex-container>
                        <flex-container :gap="null" justify-content="space-between">
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                25 t/m 44 jaar:
                            </text-decorator>
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(neighborhood.Age['25-44']) }}
                            </text-decorator>
                        </flex-container>
                        <flex-container :gap="null" justify-content="space-between">
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                45 t/m 64 jaar:
                            </text-decorator>
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(neighborhood.Age['45-64']) }}
                            </text-decorator>
                        </flex-container>
                        <flex-container :gap="null" justify-content="space-between">
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                65 jaar en ouder:
                            </text-decorator>
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(neighborhood.Age['65+']) }}
                            </text-decorator>
                        </flex-container>
                    </div>
                </flex-container>

                <flex-container
                    v-if="zipcode"
                    justify-content="center"
                >
                    <text-decorator
                        :color="Color.red500"
                        :size="FontSize.xs"
                        style="text-align: center"
                    >
                        Totalen kunnen verschillen door afwijkende meetmomenten<br> vanuit het CBS
                    </text-decorator>
                </flex-container>
                <flex-container
                    v-if="zipcode"
                    :padding="Size.md"
                >
                    <div class="emma-valuation-inline-tab-panel__neighbourhood-information">
                        <text-decorator :size="FontSize.sm" bold>
                            Bevolkingsdichtheid
                        </text-decorator>
                        <flex-container justify-content="space-between">
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(zipcode.PopulationDensitykm2) }} inwoners per km2
                            </text-decorator>
                        </flex-container>
                    </div>
                    <div class="emma-valuation-inline-tab-panel__neighbourhood-information">
                        <text-decorator :size="FontSize.sm" bold>
                            Woningtypes in deze postcode
                        </text-decorator>
                        <flex-container
                            v-for="houseType in filteredHouseTypes"
                            :key="houseType"
                            :gap="null"
                            justify-content="space-between"
                        >
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ houseTypeLabel(houseType) }}:
                            </text-decorator>
                            <text-decorator
                                :color="Color.gray400"
                                :size="FontSize.sm"
                            >
                                {{ formatNumber(zipcode.HouseTypes[houseType]) }}
                            </text-decorator>
                        </flex-container>
                    </div>
                </flex-container>
            </flex-container>
        </flex-container>
    </card-tab-panel>
</template>

<script setup lang="ts">
    import IconEuro from '@keyvue/one-material-icons/icons/euro.svg?component';
    import IconExpandMore from '@keyvue/one-material-icons/icons/expand_more.svg?component';
    import IconExpandLess from '@keyvue/one-material-icons/icons/expand_less.svg?component';
    import IconSquare from '@keyvue/one-material-icons/icons/square.svg?component';
    import IconBed from '@keyvue/one-material-icons/icons/bed.svg?component';
    import IconInfo from '@keyvue/one-material-icons/icons/info.svg?component';
    import IconSquareWithHouse from '@assets/icons/OuterSurface.svg?component';
    import IconWall from '@assets/icons/Bricks.svg?component';

    import {
        CardTabPanel,
        Size,
        IconSize,
        FontSize,
        Color,
        UnderlineBlock,
        FlexContainer,
        TextDecorator,
        SvgIcon,
        CollapseBoxDecorator,
        DataGroup,
        DataLabel,
        DataValue,
        ChipLabel,
        TextHeading,
        ButtonText,
        InputGroup,
        InputLabel,
        TextInput,
        ButtonBlockConfirm,
        StaticGoogleMaps,
        type ButtonStateChangedEvent,
        ButtonState,
    } from '@keyvue/one-ui';

    import { Lead } from '@keyvue/one-api-client';
    import { computed, ref, onMounted } from 'vue';
    import { formatDate } from '@/utils/date';
    import { formatMoney } from '@/utils/money';
    import { formatNumber } from '@/utils/number';
    import { createResourceIdentifier, ReferralAPI, User } from '@keyvue/one-api-client-legacy';
    import useClient from '@composables/useClient';

    const props = defineProps<{
        lead: Lead
    }>();

    const { client } = useClient();
    const localLead = ref(await client.get(Lead, props.lead.id));

    const showMoreTransactions = ref(false);
    const showForm = ref(false);
    const manualValuation = ref(0);

    const valuation = computed<{ [key: string]: any }>(() => localLead.value.data.valuation);
    const location = computed<{ [key: string]: any }>(() => localLead.value.data.location);
    const zipcode = computed<{ [key: string]: any }>(() => location.value?.zipcodeData);
    const neighborhood = computed<{ [key: string]: any }>(() => location.value?.neighborhoodData);
    const householdComposition = computed<{ [key: string]: any }>(() => neighborhood.value?.HouseholdComposition);
    const inhabitants = computed<{ [key: string]: any }>(() => neighborhood.value?.Inhabitants);
    const reason = computed<string>(() => localLead.value.data.reason);
    const address = computed<{ [key: string]: any }>(() => localLead.value.data.address);
    const woz = computed<{ [key: string]: any }>(() => localLead.value.data.wozValues);
    const transactions = computed<{ [key: string]: any }[]>(() => localLead.value.data.referenceTransations);
    const visibleTransactions = computed<{ [key: string]: any }[]>(() => {
        if (showMoreTransactions.value) {
            return transactions.value;
        }

        return transactions.value.slice(0, 3);
    });

    const latestWoz = computed(() => {
        if (!woz.value) {
            return null;
        }

        return woz.value.wozValues[
            Object.keys(woz.value.wozValues).sort((a, b) => (
                (new Date(a)).getTime() - (new Date(b)).getTime()
            )).pop() as string
        ];
    });

    const houseTypes = computed(() => Object.keys(zipcode.value.HouseTypes));
    const filteredHouseTypes = computed(() => houseTypes.value.filter((houseType) => (
        zipcode.value.HouseTypes[houseType] > 0
    )));

    const houseTypeLabel = (houseType: string) => {
        const houseTypeLabels = {
            Detached: 'Vrijstaande woning',
            SemiDetached: 'Twee-onder-een-kapwoning',
            LinkedSemiDetached: 'Geschakelde 2 onder 1 kapwoning',
            Linked: 'Geschakelde woning',
            Terraced: 'Tussen/rijwoning',
            Corner: 'Hoekwoning',
            End: 'Eindwoning',
            SingleFamily: 'Eengezinswoning',
            Gallery: 'Galerijflat',
            PorchFlat: 'Portiekflat',
            Corridor: 'Corridorflat',
            Maisonnette: 'Maisonnette',
            Downstairs: 'Benedenwoning',
            Upstairs: 'Bovenwoning',
            PorchHouse: 'Portiekwoning',
            MultiFamily: 'Meergezinswoning',
        };

        if (houseType in houseTypeLabels) {
            return houseTypeLabels[houseType];
        }

        return houseType;
    };

    const image = computed(() => (address.value?.imagePath ? `${import.meta.env.VITE_LEGACY_HOST}/attachments/${address.value.imagePath}` : ''));

    const mapPosition = computed(() => {
        if (valuation.value) {
            return `${valuation.value.latitude},${valuation.value.longitude}`;
        }

        if (address.value) {
            return `${address.value.latitude},${address.value.longitude}`;
        }

        return null;
    });

    const saveManualValuation = async (event: ButtonStateChangedEvent) => {
        if (!client.session.userId) {
            return;
        }

        await ReferralAPI.updateManualValuationData(
            localLead.value.id.toString(),
            Number.parseFloat(manualValuation.value.toString()),
            createResourceIdentifier('users', client.session.userId.toString()) as any as User,
        );

        await reloadLead();

        event.button.setState(ButtonState.completed);
    };

    const reloadLead = async () => {
        localLead.value = await client.get(Lead, props.lead.id);
    };

    onMounted(() => {
        const manualValuationValue = localLead.value?.data?.manualValuation?.valuation;
        const medianValuationValue = valuation.value?.medianValuation;

        if (manualValuationValue) {
            showForm.value = true;
        }

        manualValuation.value = manualValuationValue || medianValuationValue;
    });
</script>

<style scoped lang="scss">
.emma-valuation-inline-tab-panel {
    &__goal {
        position: relative;

        & > div {
            background-color: white;
            z-index: 1;
        }

        &::before {
            content: '';
            position: absolute;
            top: 50%;
            height: 2px;
            background-color: color(gray-100);
            left: 0;
            right: 0;
        }
    }

    &__header {
        padding: size(sm) size(md);
    }

    &__section {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        padding-right: size(md);
        padding-left: size(md);
        gap: size(xs);
    }

    &__images {
        display: grid;
        grid-template-columns: 1fr 1fr;

        > * {
            width: 100%;
            height: 20vh;
            object-fit: cover;
            object-position: center;
        }
    }

    &__form {
        display: flex;
        flex-direction: column;
        gap: size(sm);
        width: 100%;
        padding: size(sm) size(md);
        background-color: color(gray-100);
    }

    &__woz-grid {
        width: 100%;
        display: grid;
        gap: size(xs);
        grid-template-columns: 1fr 1fr 1fr;
        font-size: font-size(sm);
    }

    &__reference-transaction {
        border-bottom: 2px solid color(gray-200);
    }

    &__reference-transaction-information {
        padding: size(sm);
        gap: size(sm);
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
    }

    &__neighbourhood-information {
        display: flex;
        flex-direction: column;
        gap: size(xs);
        height: max-content;
        width: calc(50% - 10px);
    }
}
</style>
